import { Components } from '.generated/templates/Feature.Components.model';
import { Sitecore } from '.generated/templates/_.Sitecore.Override';
import { Text } from '@sitecore-jss/sitecore-jss-nextjs';
import { RichTextField, TextField } from '@sitecore-jss/sitecore-jss-react';
import useDictionary from 'lib/sitecore/use-dictionary';
import { classnames } from 'tailwindcss-classnames';
import ShowMoreListing from 'components/helpers/ShowMoreListing';
import Button from 'components/helpers/Button';
import Heading, { propsFromFields } from 'components/helpers/Heading';
import { MissingDataSource } from 'components/helpers/EditingHelpText';
import RichTextA11yWrapper from 'components/helpers/RichTextA11yWrapper';

type DocumentItemProps = Sitecore.Override.ItemEx & {
  fields?: {
    Title?: TextField;
    Description?: RichTextField;
  };
};

export type DocumentListProps = Sitecore.Override.ComponentBase &
  Components.Navigation.DocumentList.Fields.DocumentList & {
    fields: {
      fileList: DocumentItemProps[];
    };
  };

const DocumentItem = ({ fields, displayName, url }: DocumentItemProps): JSX.Element => {
  const dictionary = useDictionary();

  // If there is no Title field defined (as opposed to value being empty), the wrong template was selected
  // This shouldn't normally be possible, but can happen in case there is bad data.
  if (!fields?.Title) {
    return <></>;
  }

  const ctaText = dictionary.Download_DocumentList;

  return (
    <>
      <div
        className={classnames(
          'text-theme-heading-text',
          'text-base',
          'leading-3',
          'sm:text-body',
          'font-semibold'
        )}
      >
        {fields.Title?.value ? <Text field={fields.Title} editable={false} /> : displayName}
      </div>
      <div className={classnames('flex', 'justify-between')}>
        <RichTextA11yWrapper
          className={classnames('w-full', 'text-left')}
          field={fields.Description}
          editable={false}
        />
        <div className={classnames('w-full', 'text-right', 'md:pr-6')}>
          <Button
            variant="link"
            className={classnames('text-right', 'underline', 'leading-3', 'sm:leading-5')}
            linkField={{
              value: {
                text: ctaText,
                href: url,
              },
            }}
          />
        </div>
      </div>
    </>
  );
};

const DocumentList = ({ fields }: DocumentListProps) => {
  // Fail out if we don't have any fields
  const dictionary = useDictionary();

  if (!fields) {
    return <MissingDataSource />;
  }

  const seeAllCtaText = dictionary.See_All_DocumentList;
  const seeLessCtaText = dictionary.See_Less_DocumentList;

  const headingProps = propsFromFields(fields);

  return (
    <div data-component="authorable/navigation/documentlist">
      <div className={fields?.description.value ? classnames('mb-4') : ''}>
        <Heading {...headingProps} className={classnames('mb-4')} />
      </div>
      <ShowMoreListing
        items={fields.fileList}
        maxInitialDisplay={fields.maxInitialDisplay.value}
        seeAllCtaText={seeAllCtaText}
        seeLessCtaText={seeLessCtaText}
        seeAllCtaClassName={classnames('mt-6')}
        component={DocumentItem}
      />
    </div>
  );
};

export default DocumentList;
